import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Card, Button, Table, Input, Empty } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PageHeader from "../../components/PageHeader";
import AppContext from "../../components/AppContext";
import { DetailRoleModel, ModelEditRoles, AddRole, } from "../../components/Model/index-model";
import { ColumnsRole } from "../../components/Columns/columns-role";
import { getValidateMerchant } from "../../config/authsave";
import { removeSlashesFromPath } from "../../helpers/location/removeSlashesFromPath";
import { hasPermission, showToast } from "../../utils";
import Api from "../../api/Api";
import apiAuthorization from "../../services/api";

const { Search } = Input;

class Index extends React.Component {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      activeTabKey1: "tab1",
      showDetailModel: false,
      showAddModel: false,
      showEditRolePermissionsModel: false,
      selectedRecord: null,
      isLoading: false,
      dropdownPlacement: "bottomRight",
      dataTable: [],
      tableParams: {
        pagination: { current: 1, pageSize: 10, total: 0 },
        filters: {},
        sortField: null,
        sortOrder: null,
      },
    };
    this.tableRef = React.createRef();
  }

  // --- LifeCycle Methods ---
  componentDidMount() {
    this.fetchData();
    if (this.context.setFetchRoleDataCallback) this.context.setFetchRoleDataCallback(this.handleReload);
    window.addEventListener("scroll", this.handleScroll);
  }

  componentDidUpdate(_, prevState) {
    const { tableParams } = this.state;
    const { tableParams: prevTableParams } = prevState;

    const paginationChanged = tableParams.pagination.current !== prevTableParams.pagination.current || tableParams.pagination.pageSize !== prevTableParams.pagination.pageSize;

    const sortChanged = tableParams.sortOrder !== prevTableParams.sortOrder || tableParams.sortField !== prevTableParams.sortField;

    const filtersChanged = JSON.stringify(tableParams.filters) !== JSON.stringify(prevTableParams.filters);

    if (paginationChanged || sortChanged || filtersChanged) this.fetchData();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  // --- Handlers & Helpers ---
  handleReload = async () => {
    this.setLoading(true);
    try {
      await this.fetchData();
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("error", "Failed to fetch data.");
    } finally {
      this.setLoading(false);
    }
  };

  handleScroll = () => {
    const tableElement = this.tableRef.current;
    if (tableElement) {
      const isAtBottom = tableElement.getBoundingClientRect().bottom <= window.innerHeight;
      this.setState({ dropdownPlacement: isAtBottom ? "topLeft" : "bottomRight" });
    }
  };

  setLoading = (isLoading) => this.setState({ isLoading });

  // จัดการการเปิด/ปิด Modals อย่างกระชับ
  setModalState = (modalName, record = null) => {
    this.setState({
      showDetailModel: modalName === "detail",
      showAddModel: modalName === "add",
      showEditRolePermissionsModel: modalName === "edit",
      selectedRecord: record,
    });
  };

  handleCloseModals = () => this.setModalState(null);

  // --- API Calls & Logic ---
  fetchData = async () => {
    const { resProfileAdmin, setDataRole } = this.context;
    const { tableParams } = this.state;
    const valMerchant = getValidateMerchant();
    const merchantID = valMerchant?.id;
    this.setLoading(true);
    try {
      const res = await Api.getRole_permissions({ merchantId: merchantID, });

      if (res?.status === 200) {
        const resource = res?.data?.data;
        const dataArray = Array.isArray(resource) ? resource : [];

        let filteredResults = dataArray.filter((item) => item?.merchantId === resProfileAdmin?.merchantId);

        const totalLength = filteredResults.filter((item) => item.name !== "Subowner").length;

        const { UUID } = tableParams.filters || {};
        if (UUID) {
          const lowerUUID = UUID.toLowerCase();
          filteredResults = filteredResults.filter(
            (item) =>
              item.UUID?.toLowerCase().includes(lowerUUID) ||
              item.name?.toLowerCase().includes(lowerUUID)
          );
        }

        this.setState(
          (prevState) => ({
            dataTable: filteredResults,
            tableParams: {
              ...prevState.tableParams,
              pagination: {
                ...prevState.tableParams.pagination,
                total: totalLength,
              },
            },
          }),
          () => setDataRole(dataArray)
        );
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
      showToast("error", "ไม่สามารถดึงข้อมูลได้");
    } finally {
      this.setLoading(false);
    }
  };

  // ตรวจสอบสิทธิ์การลบ (ปรับปรุงให้สั้นลงและเข้าใจง่าย)
  checkDeleteAllowed = (record) => {
    const { resProfileAdmin } = this.context;

    // ห้ามลบ Role ตัวเอง
    if (resProfileAdmin?.roleID === record?.id) return false;

    const currentAdminRole = resProfileAdmin?.role;
    const currentAdminType = resProfileAdmin?.admin_type;
    const targetRole = record?.name;
    const targetType = record?.admin_type;
    const adminMerchantId = record?.merchantId;

    const superPrivilegeRoles = ["Owner", "Subowner"];
    const superPrivilegeTypes = ["programmer", "superadmin", "agent"];

    // 1. ระดับ Super User -> ลบได้ทุกคน
    if (superPrivilegeRoles.includes(currentAdminRole) || superPrivilegeTypes.includes(currentAdminType)) return true;

    // 2. ถ้าเป้าหมายเป็น Super User -> ผู้ใช้งานทั่วไปไม่มีสิทธิ์ลบ
    const isTargetSuperUser = superPrivilegeRoles.includes(targetRole) || superPrivilegeTypes.includes(targetType);

    if (isTargetSuperUser) return false;

    // 3. พนักงานระดับบริหารประจำ Merchant
    const subRoles = [
      `Owner${adminMerchantId}`,
      `Subowner${adminMerchantId}`,
      `Head_Finance${adminMerchantId}`,
      `Head_Admin${adminMerchantId}`,
    ];

    if (subRoles.includes(currentAdminRole)) return true;

    return !subRoles.includes(targetRole);
  };

  handleDelete = (record) => {
    if (!record) return alert(this.props.t("No data"));

    if (!this.checkDeleteAllowed(record)) {
      return Swal.fire({
        icon: "error",
        title: "Access denied.",
        text: "You do not have permission to delete a super-level Role or an active Role.",
        confirmButtonText: this.props.t("ok"),
      });
    }

    const { name, id } = record;

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete? Role: ${name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: this.props.t("confirm"),
      cancelButtonText: this.props.t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        this.setLoading(true);
        try {
          const response = await apiAuthorization.delete(`/api/v1/admin/deleteRole/${id}`);
          const data = response.data;

          if (data.success) {
            showToast("success", this.props.t(data.message || "customer_deleted_success"));
            this.handleReload();
          } else {
            showToast("error", data.message || this.props.t("failed_to_delete_customer"));
          }
        } catch (error) {
          console.error("Error deleting role:", error);
          showToast("error", this.props.t("error_deleting_customer"));
        } finally {
          this.setLoading(false);
        }
      }
    });
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState((prevState) => ({
      tableParams: {
        ...prevState.tableParams,
        pagination: {
          ...prevState.tableParams.pagination,
          current: pagination.current,
          pageSize: pagination.pageSize,
        },
        filters,
        sortField: Array.isArray(sorter) ? sorter[0]?.field : sorter.field,
        sortOrder: Array.isArray(sorter) ? sorter[0]?.order : sorter.order,
      },
    }));
  };

  onSearch = (value) => {
    this.setState((prevState) => ({
      tableParams: {
        ...prevState.tableParams,
        pagination: { ...prevState.tableParams.pagination, current: 1 },
        filters: { ...prevState.tableParams.filters, UUID: value },
      },
    }));
  };

  onTab1Change = (key) => this.setState({ activeTabKey1: key });

  render() {
    const { t } = this.props;
    const {
      resAllDataMerchang,
      resDataRole,
      permissionData,
      resProfileAdmin,
      fetchPromiseData,
    } = this.context;

    const {
      activeTabKey1,
      showDetailModel,
      selectedRecord,
      showAddModel,
      isLoading,
      tableParams,
      dataTable,
      showEditRolePermissionsModel,
      dropdownPlacement,
    } = this.state;

    const valMerchant = getValidateMerchant();
    const merchantID = valMerchant?.id;
    const nameMerchang = valMerchant?.name;

    const accessible = ["programer"].includes(resProfileAdmin?.admin_type);
    const displayDataTable = accessible ? dataTable : dataTable.filter((item) => item.name !== "Subowner");



    const filteredPermissions = permissionData.filter((item) => item.name !== "manage_invoices_view");

    const userPermissions = Array.isArray(resProfileAdmin?.permissions) ? resProfileAdmin.permissions : [];

    const accessible_details = hasPermission(userPermissions, "details_role");
    const accessible_add = hasPermission(userPermissions, "add_role");
    const accessible_edit = hasPermission(userPermissions, "edit_role");
    const accessible_del = hasPermission(userPermissions, "del_role");

    const columns = ColumnsRole(
      nameMerchang,
      dropdownPlacement,
      (record) => this.setModalState("detail", record),
      (record) => this.setModalState("edit", record),
      this.handleDelete,
      resProfileAdmin,
      accessible_details,
      accessible_edit,
      accessible_del
    );

    const tabList = [{ key: "tab1", tab: t("role_list") }];

    const contentList = {
      tab1: (
        <div className="body table-responsive">
          <Table
            className="col-lg-12"
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("No data")}
                />
              ),
            }}
            rowKey={(record) => record.id || record.UUID}
            onChange={this.handleTableChange}
            dataSource={displayDataTable}
            loading={isLoading}
            pagination={{
              ...tableParams.pagination,
              showSizeChanger: true,
              locale: { items_per_page: `/ ${t("paged")}` },
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
      ),
    };

    return (
      <>
        <DetailRoleModel
          handleClose={this.handleCloseModals}
          isData={selectedRecord}
          show={showDetailModel}
          roleData={dataTable}
          nameMerchang={nameMerchang}
          isMerchantData={valMerchant}
          t={t}
        />
        <AddRole
          handleClose={this.handleCloseModals}
          show={showAddModel}
          isLoad={isLoading}
          resAllDataMerchang={resAllDataMerchang}
          nameMerchang={nameMerchang}
          merchantId={merchantID}
          fetchData={this.fetchData}
          resDataRole={resDataRole}
          isMerchantData={valMerchant}
          resProfileAdmin={resProfileAdmin}
          permissionAll={accessible ? permissionData : filteredPermissions}
          t={t}
        />
        <ModelEditRoles
          handleClose={this.handleCloseModals}
          isData={selectedRecord}
          show={showEditRolePermissionsModel}
          showToast={showToast}
          isMerchantData={valMerchant}
          nameMerchang={nameMerchang}
          fetchData={this.fetchData}
          fetchPromiseData={fetchPromiseData}
          permissionAll={accessible ? permissionData : filteredPermissions}
          t={t}
        />

        <div style={{ flex: 1 }}>
          <div className="container-fluid">
            <PageHeader
              HeaderText={t("role")}
              Breadcrumb={[
                {
                  name: removeSlashesFromPath(window.location.pathname),
                  navigate: window.location.pathname,
                },
              ]}
            />
            <br />
            <Card
              title={
                accessible_add ? (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => this.setModalState("add")}
                  >
                    {t("add")}
                  </Button>
                ) : null
              }
              extra={
                <Search
                  placeholder={t("search...")}
                  onSearch={this.onSearch}
                  enterButton
                />
              }
              tabList={tabList}
              activeTabKey={activeTabKey1}
              onTabChange={this.onTab1Change}
            >
              {contentList[activeTabKey1]}
            </Card>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = ({ mailInboxReducer }) => ({
  isInbox: mailInboxReducer.isInbox,
});

export default connect(mapStateToProps, {})(withTranslation()(Index));